import {
  Dashboard as DashboardIcon,
  DashboardCustomize,
  ErrorOutline,
  KeyboardArrowDown,
  SettingsInputComponent,
  ArrowRight,
  KeyboardArrowRight,
  Logout,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputBase,
  Menu,
  MenuItem,
  Radio,
  Select,
  Snackbar,
} from "@mui/material";
import axios from "axios";
import { setTokensFromResponse } from "../utils/authTokens";
import React, { useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate, Navigate } from "react-router-dom";
import LoadingAnimation from "./reusables/LoadingAnimation";
import CallDetails from "./dashboard/CallDetails";
import CallRoute from "./dashboard/CallRoute";
import CallRoutings from "./dashboard/CallRoutings";
import Channels from "./dashboard/Channels";
import Dailer from "./dashboard/Dailer";
import Home from "./dashboard/Home";
import Recordings from "./dashboard/Recordings";
import Settings from "./dashboard/Settings";
import {
  ChevronRight,
  Settings as SettingIcon,
  Smartphone,
  X,
} from "lucide-react";
import Sidebar from "./layout/Sidebar";
import { checkIfUserHasRelatedOrg } from "./utils/checkUserOrgStatus";
import ChangeOrgModal from "./modals/ChangeOrgModal";
import { identifyPostHogUser } from "../utils/posthogAnalytics";
import Front from "@frontapp/plugin-sdk";
import FrontPluginDefault from "./dashboard/FrontPluginDefault";
import NewCallRoute from "./dashboard/NewCallRoute";
import {
  ChartNoAxesColumnIncreasing,
  Mic,
  PhoneOutgoing,
  SlidersVertical,
} from "lucide-react";
import { Route as RouteIcon, History } from "lucide-react";
import ChangeOrgProfile from "./reusables/ChangeOrgProfile";
import ChangeUserProfile from "./reusables/ChangeUserProfile";
import SwitchToOldUIModal from "./modals/SwitchToOldUIModal";
import CallStatus from "./reusables/CallStatus";
import Logo from "./reusables/Logo";
import Contacts from "./dashboard/Contacts";
import ContactDetailPage from "./dashboard/ContactDetailPage";
import NotFound from "./dashboard/NotFound";
import { fetchAndSetOrganization, getOrgIdFromUser } from "../utils/organizationService";

const Dashboard = () => {
  let latestContext;

  const navigate = useNavigate();

  //front logic

  const [conversation, setConversation] = useState();
  const [lastContextId, setLastContextId] = useState(null);
  const [lastEntryPoint, setLastEntryPoint] = useState(null);

  useEffect(() => {
    let subscribe = Front.contextUpdates.subscribe((context) => {
      latestContext = context;

      // Check if context ID has changed OR if entry point has changed
      const currentContextId = context.id;
      const currentEntryPoint = context.entryPoint?.type;
      const currentHandle = context.entryPoint?.data?.handle;

      const contextChanged = currentContextId !== lastContextId;
      const entryPointChanged = currentEntryPoint !== lastEntryPoint?.type;
      const handleChanged = currentHandle !== lastEntryPoint?.data?.handle;

      // Process if context changed OR if entry point/handle changed within same context
      const shouldProcess =
        contextChanged || entryPointChanged || handleChanged;

      if (!shouldProcess) {
        return;
      }

      if (contextChanged) {
        setLastContextId(currentContextId);
      }

      // Get previous handle for comparison
      const previousHandle = lastEntryPoint?.data?.handle;

      // Store the current entry point for next comparison
      setLastEntryPoint(context.entryPoint);

      // Check if we should navigate to dialer
      const shouldNavigateToDialer =
        currentEntryPoint === "CONTACT_HANDLE" &&
        (previousHandle === null || previousHandle !== currentHandle);

      if (shouldNavigateToDialer) {
        navigate("/dashboard/dialer");
        return;
      }

      // For all other cases, check if this is a Twilanswer conversation
      latestContext
        .listMessages()
        .then((response) => {
          let link;
          if (response.results.length > 0) {
            const latestMessageIndex = response.results.length - 1;
            let body = response.results[latestMessageIndex].content.body;
            const path = body.match(/twilanswer\.com(\/[^"]+)/)?.[1];
            link = path;
          }

          setConversation(context.conversation.id);

          if (link) {
            navigate(link);
          } else {
            navigate("/dashboard/front");
          }
        })
        .catch((error) => {
          // Log for production diagnostics when Front listMessages fails
          console.error("[Front Context] Error in listMessages:", error);
          navigate("/dashboard/front");
        });
    });

    return () => {
      subscribe.unsubscribe();
    };
  }, [lastContextId, lastEntryPoint]);

  const [openChangeOrg, setOpenChangeOrg] = useState(false);

  const location = useLocation();
  const checks = async () => checkIfUserHasRelatedOrg();

  const getOrg = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization")).orgId.S;
    await fetchAndSetOrganization(orgId);
  };

  useEffect(() => {
    checks().then((res) => {
      if (res === 'not_member') {
        console.info("[Auth] Showing 'no org access' modal: current user is not a member of the current organization.");
        setOpenChangeOrg(true);
      } else if (res === 'member') {
        getOrg();
      }
      // res === 'unknown': do not open org modal (request failed; 401 handled by interceptor)
    });
  }, [location.pathname]);

  const [openSidebar, setOpenSidebar] = useState(false);
  const [showSwitchToOldUI, setShowSwitchToOldUI] = useState(false);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));

  // Check if current user has type "user" for the current organization
  const isUserType = () => {
    try {
      const organization = JSON.parse(localStorage.getItem("organization"));
      const currentUser = JSON.parse(localStorage.getItem("user"));
      
      if (!organization?.users?.L || !currentUser?.userId?.S) {
        return false; // Default to showing settings if we can't determine
      }

      const currentUserInOrg = organization.users.L.find(
        (item) => item?.M?.userId?.S === currentUser.userId.S
      );

      return currentUserInOrg?.M?.type?.S === "user";
    } catch (error) {
      // Log for production diagnostics when user type check fails
      console.error("Error checking user type:", error);
      return false; // Default to showing settings on error
    }
  };

  const userIsUserType = isUserType();

  const [orgs, setOrgs] = useState();
  // Loading state (uncomment to show full-screen loader until orgs load)
  // const [loadState, setLoadState] = useState(true);

  useEffect(() => {
    const loadOrgs = async () => {
      const { getAllOrgs } = await import('../utils/getAllOrgs');
      try {
        await getAllOrgs(setOrgs);
      } catch (error) {
        // Log for production diagnostics when org list fails to load
        console.error('[Dashboard] Error loading organizations:', error);
      }
      // finally { setLoadState(false); }
    };
    loadOrgs();
  }, []);

  //check if user has twilio_information and exist

  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);
  const changeOrganization = async (org_id) => {
    try {
      await fetchAndSetOrganization(org_id);
      window.location.reload();
    } catch (error) {
      // Log for production diagnostics when change organization fails
      console.error("Dashboard: change organization failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrorMsg(error.response.data.message);
      }
      setLoader(false);
    }
  };

  useEffect(() => {
    let organization = JSON.parse(localStorage.getItem("organization"));
    if (!organization) {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      const orgId = getOrgIdFromUser(storedUser);
      if (orgId) {
        fetchAndSetOrganization(orgId).catch(() => {
          console.info("[Auth] Redirecting to login: no organization in storage (dashboard load).");
          const from = window.location.pathname + window.location.search;
          window.location.href =
            from === "/" ? "/login" : `/login?redirect=${encodeURIComponent(from)}`;
        });
        return;
      }
      console.info("[Auth] Redirecting to login: no organization in storage (dashboard load).");
      const from = window.location.pathname + window.location.search;
      window.location.href =
        from === "/" ? "/login" : `/login?redirect=${encodeURIComponent(from)}`;
    } else if (!organization.twilio_integration) {
      console.info("[Auth] Redirecting to register: organization has no Twilio integration (dashboard load).");
      window.location.pathname = "/register";
    } else {
      identifyPostHogUser(user, organization);
    }
  }, []); // Runs only once when the component mounts

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [showCreateNewOrganization, setShowCreateNewOrganization] =
    useState(false);

 const [successMsg, setSuccessMsg] = useState(null);
    const [user_, setUser_] = useState({
    organization_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userId: JSON.parse(localStorage.getItem("user"))?.userId?.S || null,
    email: JSON.parse(localStorage.getItem("user"))?.email?.S || null,
    password: JSON.parse(localStorage.getItem("user"))?.password?.S || null,
    first_name: JSON.parse(localStorage.getItem("user"))?.fName?.S || null,
    last_name: JSON.parse(localStorage.getItem("user"))?.lName?.S || null,
    company_name:
      JSON.parse(localStorage.getItem("organization"))?.organization_name?.S ||
      "",
    agreed: false,
    registeredFromGoogle:
      JSON.parse(localStorage.getItem("user"))?.registeredFromGoogle?.BOOL ||
      false,
  });

  const addOrganization = async () => {
    setErrorMsg(null);
    if (user_.company_name.length >= 1) {
      setLoader(true);
      //console.log(JSON.parse(localStorage.getItem('user'))?.userId?.S)
      //console.log('user__',user_)
      await axios
        .post(
          process.env.REACT_APP_BACKEND_URL + "user/add-created-user-to-org",
          {
            ...user_,
            userId: JSON.parse(localStorage.getItem("user"))?.userId?.S,
          }
        )
        .then((res) => {
          if (res.data.status === "success") {
            localStorage.setItem("user", JSON.stringify(res.data.user)); //store user data in local storage
            localStorage.setItem(
              "organization",
              JSON.stringify(res.data.organization)
            ); //stores organization datat in local storage
            setTokensFromResponse(res.data);
            setSuccessMsg(
              "Organization has been created, we would now redirect you to complete onboarding flow. "
            );
            navigate("/register?neworg=true");
            setLoader(false);
           
          } else {
            setErrorMsg(`${res.data.msg}`);
            setLoader(false);
          }
        })
        .catch((err) => {
          setLoader(false);
        });
    } else {
      setErrorMsg("Please enter company name for your organization");
    }
  };
  return (
    <div>
      <ChangeOrgModal
        open={openChangeOrg}
        setOpen={setOpenChangeOrg}
        user={user}
        changeOrganization={changeOrganization}
        orgs={orgs}
      />
      <Snackbar
              open={!!successMsg}
              autoHideDuration={6000}
              onClose={() => setSuccessMsg(null)}
              message={
                successMsg
              }
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              ContentProps={{
                sx: {
                  background: "linear-gradient(45deg, #333, #444)",
                  color: "#fff",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
                  padding: "8px 16px",
                  animation: "slideIn 0.3s ease-out",
                },
              }}
            />
      <Sidebar open={openSidebar} setOpen={setOpenSidebar} orgs={orgs} />
      {/* Full-screen loading (uncomment loadState + setLoadState in effect finally, then uncomment this block)
      {loadState ? (
        <div
          className="bg-white"
          style={{
            width: "100vw",
            height: "100vh",
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box>
            <div className="flex items-center gap-3 text-black text-[15px] font-semibold">
              <LoadingAnimation style={{ width: 48, height: 48 }} />
              <span>Twilanswer Loading</span>
            </div>
          </Box>
        </div>
      ) : (
      */}
        <>
          <div
            className=" flex items-center xl:justify-between px-4 xl:hidden bg-white 
      xl:bg-transparent pb-4 pt-4 xl:pt-0 xl:pb-0 rounded-br-[20px] rounded-bl-[20px] xl:rounded-br-[20px] xl:rounded-bl-[20px] 
      "
          >
            <MenuIcon
              onClick={() => {
                setOpenSidebar(!openSidebar);
              }}
              sx={{
                color: { lg: "black", xs: "#FFCC00" },
              }}
            />

            <div className="text-[20px] font-normal  text-center mx-auto flex items-center space-x-2  ">
              <img src="/icons/login_img.png" className="w-[30px] hidden" />
              <Logo size="md" className="h-[70px]" />
            </div>
          </div>

          <div className="grid grid-cols-12">
            <div className="col-span-2 w-[230px] flex-col xl:max-h-[100vh] h-full hidden xl:flex ">
              <div className="bg-white relative border border-r-[1px] xl:min-h-[100vh] h-full ">
<div className="text-[20px] px-6 py-4 font-normal flex justify-start mb-0">
                <Logo size="sm" />
              </div>
                <Divider sx={{ mb: 2 }} />
                <div className="text-[14px]  space-y-3 px-3  flex-1">
                  {location.pathname === "/dashboard" ||
                  location.pathname.startsWith("/dashboard/call-details") ? (
                    <div
                      onClick={() => {
                        navigate("/dashboard");
                      }}
                      className="cursor-pointer flex items-center text-[#605CFF] font-semibold pl-6 rounded-[8px] bg-[#EEF2FF]    py-2 px-3  "
                    >
                      {" "}
                      <ChartNoAxesColumnIncreasing className="w-[20px] mr-2" />{" "}
                      Dashboard{" "}
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer flex items-center py-2 px-3  text-black   pl-6 rounded-[8px] "
                      onClick={() => {
                        navigate("/dashboard");
                      }}
                    >
                      {" "}
                      <ChartNoAxesColumnIncreasing className="w-[20px] mr-2" />{" "}
                      Dashboard{" "}
                    </div>
                  )}

                  {location.pathname.startsWith("/dashboard/dialer") ? (
                    <div
                      onClick={() => {
                        navigate("/dashboard/dialer");
                      }}
                      className="cursor-pointer flex items-center text-[#605CFF]  font-semibold pl-6 rounded-[8px] bg-[#EEF2FF] py-2 px-3"
                    >
                      {" "}
                      <PhoneOutgoing className="w-[20px] mr-2" /> Dialer{" "}
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer flex items-center py-2 px-3   pl-6 rounded-[8px] "
                      onClick={() => {
                        navigate("/dashboard/dialer");
                      }}
                    >
                      {" "}
                      <PhoneOutgoing className="w-[20px] mr-2" /> Dialer{" "}
                    </div>
                  )}

                  {location.pathname.startsWith("/dashboard/call-routings") ||
                  location.pathname.startsWith("/dashboard/call-route") ||
                  location.pathname.startsWith("/dashboard/create-route") ? (
                    <div
                      onClick={() => {
                        navigate("/dashboard/call-routings");
                      }}
                      className="cursor-pointer flex items-center text-[#605CFF] font-semibold pl-6 rounded-[8px] bg-[#EEF2FF] py-2 px-3"
                    >
                      {" "}
                      <RouteIcon className="w-[20px] mr-2" /> Call Routing{" "}
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer flex items-center py-2 px-3  pl-6 rounded-[8px] "
                      onClick={() => {
                        navigate("/dashboard/call-routings");
                      }}
                    >
                      {" "}
                      <RouteIcon className="w-[20px] mr-2" /> Call Routing{" "}
                    </div>
                  )}

                  {location.pathname.startsWith("/dashboard/channels") ? (
                    <div
                      onClick={() => {
                        navigate("/dashboard/channels");
                      }}
                      className="cursor-pointer flex items-center text-[#605CFF] font-semibold pl-6 rounded-[8px] bg-[#EEF2FF] py-2 px-3"
                    >
                      {" "}
                      <SlidersVertical className="w-[20px] mr-2" /> Channels{" "}
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer flex items-center py-2 px-3  pl-6 rounded-[8px] "
                      onClick={() => {
                        navigate("/dashboard/channels");
                      }}
                    >
                      {" "}
                      <SlidersVertical className="w-[20px] mr-2" /> Channels{" "}
                    </div>
                  )}

                  {location.pathname.startsWith("/dashboard/recordings") ? (
                    <div
                      onClick={() => {
                        navigate("/dashboard/recordings");
                      }}
                      className=" cursor-pointer flex items-center text-[#605CFF] font-semibold pl-6 rounded-[8px] bg-[#EEF2FF] py-2 px-3"
                    >
                      {" "}
                      <Mic className="w-[20px] mr-2" /> Recordings{" "}
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer flex items-center py-2 px-3  pl-6 rounded-[8px] "
                      onClick={() => {
                        navigate("/dashboard/recordings");
                      }}
                    >
                      {" "}
                      <Mic className="w-[20px] mr-2" /> Recordings{" "}
                    </div>
                  )}

                  {!userIsUserType && (
                    <>
                      {location.pathname.startsWith("/dashboard/settings") ? (
                        <div
                          onClick={() => {
                            navigate("/dashboard/settings");
                          }}
                          className="cursor-pointer flex items-center text-[#605CFF] font-semibold pl-6 rounded-[8px] bg-[#EEF2FF] py-2 px-3"
                        >
                          {" "}
                          <SettingIcon
                            src="/icons/settings-active.png"
                            className="w-[20px] mr-2"
                          />{" "}
                          Settings{" "}
                        </div>
                      ) : (
                        <div
                          className="cursor-pointer flex items-center py-2 px-3  pl-6 rounded-[8px] "
                          onClick={() => {
                            navigate("/dashboard/settings");
                          }}
                        >
                          {" "}
                          <SettingIcon
                            src="/icons/settings-inactive.png"
                            className="w-[20px] mr-2"
                          />{" "}
                          Settings{" "}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {err_msg && (
                  <div className="text-center text-red-500 lg:text-[13px] text-[12px] mt-2  font-semibold ">
                    <ErrorOutline sx={{ fontSize: "" }} /> {err_msg}
                  </div>
                )}

                {/* <div className="mt-4">
                  {loader ? (
                    <CircularProgress size={"1.3rem"} sx={{ color: "blue" }} />
                  ) : (
                    <Select
                      IconComponent={KeyboardArrowDown}
                      fullWidth
                      value={
                        user?.relatedOrgs.L.filter(
                          (e) =>
                            e?.M.org_id.S ===
                            JSON.parse(localStorage.getItem("organization")).orgId.S
                        )[0]?.M.org_id.S
                      }
                      onChange={(f) => {
                        changeOrganization(f.target.value);
                      }}
                      className="mb-2 bg-[#F7F7F8]"
                      sx={{
                        fontSize: "13px",
                        padding: "2px 8px", // Adjust padding here
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        "& .MuiSelect-select": {
                          padding: "2px 8px", // Adjust padding for the text inside Select

                        },
                      }}
                    >
                      {user?.relatedOrgs.L.map((item, index) => {
                        return (
                          <MenuItem key={index} value={item?.M.org_id.S}>{orgs?.find((e) => e.orgId.S === item?.M.org_id.S)?.organization_name.S}</MenuItem>
                        );
                      })}
                    </Select>
                  )}
 
                </div> */}

                <div className="w-[100%]   absolute  bottom-1   rounded-lg">
                  <Divider sx={{ mb: 2 }} />
                  {/* <div className="px-3 w-full mb-5">
                    <CallStatus changeOrganization={changeOrganization} />
                  </div> */}
                  {showCreateNewOrganization && (
                    <div className="w-full px-3">
                      <div className="space-y-2 mb-5 mt-6">
                        <div className="flex items-center justify-between">
                          <div className=" lg:text-[13px] text-[13px] text-left ">
                            Company name
                          </div>

                          <X
                            className="w-4 h-4 text-gray-400 cursor-pointer"
                            onClick={() => {
                              setShowCreateNewOrganization(false);
                            }}
                          />
                        </div>
                        <InputBase
                          placeholder="Organization name"
                          onChange={(e) => {
                            setUser_({ ...user_, company_name: e.target.value });
                          }}
                          sx={{
                            bgcolor: "#F7F7F8",
                            width: "100%",
                            px: 2,
                            py: "2px",
                            fontSize: "14px",
                          }}
                        />
                      </div>

                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          addOrganization();
                        }}
                        disabled={loader}
                        sx={{
                          borderRadius: "4px",
                          py: "5px",
                          mb: "10px",
                          fontSize:'12px',
                        }}
                      >
                        {loader ? (
                          <CircularProgress
                            size={20}
                            thickness={4}
                            sx={{
                              color: "#fff",
                            }}
                          />
                        ) : (
                          "Create New Organization"
                        )}
                      </Button>
                    </div>
                  )}

                  <div className="px-3 w-full mb-3">
                    <button
                      type="button"
                      onClick={() => setShowSwitchToOldUI(true)}
                      className="w-full flex items-center gap-2 py-2 px-3 rounded-lg text-[13px] text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-colors"
                    >
                      <History className="w-4 h-4 text-gray-500" />
                      Use Classic View
                    </button>
                  </div>

                  <SwitchToOldUIModal
                    open={showSwitchToOldUI}
                    onClose={() => setShowSwitchToOldUI(false)}
                  />

                  <div className="px-3 w-full mb-5">
                    <ChangeOrgProfile
                      changeOrganization={changeOrganization}
                      setShowCreateNewOrganization={
                        setShowCreateNewOrganization
                      }
                    />
                  </div>

                  <div className="px-3 w-full">
                    <ChangeUserProfile
                      changeOrganization={changeOrganization}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="xl:col-span-10 col-span-12 min-w-0 pb-2 xl:max-h-[100vh] overflow-auto ">
              <Routes>
                <Route path="/front" element={<FrontPluginDefault />} />
                <Route path="/" element={<Home />} />
                <Route
                  path="/call-details/:call_id"
                  element={<CallDetails conversation={conversation} />}
                />
                <Route path="/call-routings" element={<CallRoutings />} />
                <Route
                  path="/call-route-old/:route_id"
                  element={<CallRoute navigate={navigate} />}
                />
                <Route
                  path="/create-route-old"
                  element={<CallRoute navigate={navigate} />}
                />
                <Route
                  path="/create-route"
                  element={<NewCallRoute navigate={navigate} />}
                />
                <Route
                  path="/call-route/:route_id"
                  element={<NewCallRoute navigate={navigate} />}
                />
                <Route path="/dialer" element={<Dailer />} />
                <Route path="/channels" element={<Channels />} />
                <Route path="/recordings" element={<Recordings />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route
                  path="/contact/:phoneNumber"
                  element={<ContactDetailPage />}
                />
                <Route path="/settings/*" element={<Settings />} />
                <Route path="/not-found" element={<NotFound />} />
                {/* catch-all redirect */}
                <Route path="*" element={<Navigate to="/not-found" replace />} />
              </Routes>
            </div>
          </div>
        </>
      {/* )} */}
    </div>
  );
};

export default Dashboard;
